import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Wifi, Waves, Sprout, Coffee, Shirt, Sun, MapPin } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageProvider";
import { useFx, formatIDR } from "@/i18n/FxProvider";
import { IMG, ROOM_HERO } from "@/lib/images";
import { ROOMS } from "@/lib/rooms";
import { Reveal, SectionTitle } from "@/components/Reveal";
import { waUrl, BOOK_MESSAGE, ORDER_MESSAGE, LAUNDRY_MESSAGE } from "@/lib/contact";

const ICONS = [Sprout, Waves, Wifi, MapPin, Coffee, Shirt];

// Teasers for homepage room preview — pick 3 representative rooms
const ROOM_TEASERS = [
  { img: ROOM_HERO["standard-garden"], room: ROOMS.find((r) => r.id === "standard-garden") },
  { img: ROOM_HERO["deluxe-pool"], room: ROOMS.find((r) => r.id === "deluxe-pool") },
  { img: ROOM_HERO["big-room"], room: ROOMS.find((r) => r.id === "big-room") },
];

export default function Home() {
  const { t, lang } = useLang();
  const fx = useFx();

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="container-px mx-auto pt-6 pb-12 md:pb-20 lg:pb-24">
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-7 pt-6 md:pt-10">
              <Reveal>
                <div className="eyebrow mb-6 flex items-center gap-3">
                  <span className="inline-block w-8 h-px bg-cocoa" />
                  {t.hero.eyebrow}
                </div>
                <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] leading-[0.95] tracking-tight text-ink text-balance">
                  {t.hero.title.split(" ").slice(0, -2).join(" ")}{" "}
                  <span className="italic text-moss">{t.hero.title.split(" ").slice(-2).join(" ")}</span>
                </h1>
                <p className="mt-7 max-w-xl text-lg sm:text-xl text-ink/70 font-light leading-relaxed">
                  {t.hero.subtitle}
                </p>
                <div className="mt-10 flex flex-wrap gap-3">
                  <a href={waUrl(BOOK_MESSAGE())} target="_blank" rel="noreferrer" data-testid="hero-cta-book"
                    className="inline-flex items-center gap-2 rounded-full bg-moss px-6 py-3.5 text-white text-sm hover:bg-mossLight shadow-md hover:-translate-y-0.5 transition-all">
                    {t.hero.ctaBook} <ArrowRight className="w-4 h-4" />
                  </a>
                  <Link to="/restaurant" data-testid="hero-cta-menu"
                    className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-6 py-3.5 text-sm hover:bg-white/70 transition-all">
                    {t.hero.ctaMenu}
                  </Link>
                  <Link to="/laundry" data-testid="hero-cta-laundry"
                    className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-6 py-3.5 text-sm hover:bg-white/70 transition-all">
                    {t.hero.ctaLaundry}
                  </Link>
                  <Link to="/contact" data-testid="hero-cta-contact"
                    className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm text-ink/80 hover:text-moss transition-all">
                    {t.hero.ctaContact} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-5 relative">
              <Reveal delay={0.1}>
                <div className="relative">
                  <div className="absolute -top-10 -left-6 w-40 h-40 rounded-full bg-sage/20 blur-3xl" />
                  <div className="grid grid-cols-2 gap-4">
                    <div className="overflow-hidden rounded-3xl aspect-[3/4]">
                      <img src={IMG.heroPool} alt="Swimming pool at Agung Wiwin Homestay" className="w-full h-full object-cover animate-ken-burns" />
                    </div>
                    <div className="flex flex-col gap-4 pt-10">
                      <div className="overflow-hidden rounded-3xl aspect-square">
                        <img src={IMG.heroGarden} alt="Tropical garden" className="w-full h-full object-cover" />
                      </div>
                      <div className="overflow-hidden rounded-3xl aspect-[4/5]">
                        <img src={IMG.heroRoom} alt="Boutique guest room" className="w-full h-full object-cover" />
                      </div>
                    </div>
                  </div>
                  {/* FX widget */}
                  <div className="absolute -bottom-8 -left-4 glass rounded-2xl px-4 py-3 shadow-md" data-testid="fx-widget">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-cocoa">{t.common.liveRate}</div>
                    <div className="font-display text-xl text-ink">
                      1 USD ≈ {new Intl.NumberFormat("en-US").format(Math.round(fx.rate || 15500))} IDR
                    </div>
                    <div className="text-[10px] text-ink/60">{fx.source === "live" ? "Live" : "Reference"} rate</div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT — new welcome copy */}
      <section className="section-py" data-testid="section-about">
        <div className="container-px mx-auto grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <SectionTitle eyebrow={t.welcome.eyebrow} title={t.welcome.title} />
          </div>
          <div className="lg:col-span-7 space-y-6 text-ink/80 leading-relaxed text-lg font-light">
            <Reveal><p>{t.welcome.p1}</p></Reveal>
            <Reveal delay={0.05}><p>{t.welcome.p2}</p></Reveal>
            <Reveal delay={0.1}><p>{t.welcome.p3}</p></Reveal>
            <Reveal delay={0.15}>
              <p className="font-display text-2xl text-moss italic leading-snug pt-3">
                {t.welcome.tagline}
              </p>
              <p className="text-ink/70 italic mt-2">{t.welcome.closing}</p>
            </Reveal>
            <Reveal delay={0.2} className="grid grid-cols-2 gap-4 pt-6">
              <div className="overflow-hidden rounded-3xl aspect-[4/5]">
                <img src={IMG.gardenLush} alt="Lush tropical garden" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="overflow-hidden rounded-3xl aspect-[4/5] mt-10">
                <img src={IMG.poolWide} alt="Outdoor swimming pool" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="section-py bg-sandDeep/40" data-testid="section-why">
        <div className="container-px mx-auto">
          <SectionTitle eyebrow={t.why.eyebrow} title={t.why.title} />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {t.why.items.map((item, i) => {
              const Icon = ICONS[i] || Sun;
              return (
                <Reveal key={item.t} delay={i * 0.05}>
                  <div className="h-full p-7 rounded-3xl bg-white/70 backdrop-blur border border-white hover:shadow-lg transition-all duration-500 hover:-translate-y-1">
                    <div className="w-11 h-11 rounded-full bg-moss/10 text-moss grid place-items-center mb-5">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="font-display text-xl text-ink">{item.t}</div>
                    <p className="mt-3 text-ink/70 leading-relaxed">{item.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ROOMS TEASER */}
      <section className="section-py" data-testid="section-rooms-teaser">
        <div className="container-px mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <SectionTitle eyebrow={t.rooms.eyebrow} title={t.rooms.title} subtitle={t.rooms.subtitle} />
            <Link to="/rooms" className="inline-flex items-center gap-2 text-moss hover:gap-3 transition-all">
              {t.common.viewRooms} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {ROOM_TEASERS.map(({ img, room }, i) => (
              <Reveal key={room.id} delay={i * 0.06}>
                <Link to="/rooms" className="block group">
                  <div className="overflow-hidden rounded-3xl aspect-[4/5]">
                    <img src={img} alt={room.name[lang]} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="mt-5 flex items-end justify-between gap-4">
                    <div>
                      <div className="font-display text-xl text-ink">{room.name[lang]}</div>
                      <div className="text-ink/60 text-sm mt-1">
                        {t.common.from} {formatIDR(room.pricing.low.noBf, lang)} {t.common.perNight}
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-moss group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* RESTAURANT + LAUNDRY split */}
      <section className="section-py" data-testid="section-services">
        <div className="container-px mx-auto grid lg:grid-cols-2 gap-6">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl group h-[420px]">
              <img src={IMG.restaurantHero} alt="The Garden Cafe" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/30 to-transparent" />
              <div className="relative z-10 h-full flex flex-col justify-end p-8 text-white">
                <div className="eyebrow text-white/80">{t.restaurant.eyebrow}</div>
                <h3 className="font-display text-3xl mt-2 max-w-md">{t.restaurant.title}</h3>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/restaurant" className="inline-flex items-center gap-2 rounded-full bg-white text-ink px-5 py-2.5 text-sm hover:bg-cream">
                    {t.common.viewMenu}
                  </Link>
                  <a href={waUrl(ORDER_MESSAGE())} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-wa text-white px-5 py-2.5 text-sm">
                    {t.common.orderNow}
                  </a>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <div className="relative overflow-hidden rounded-3xl group h-[420px]">
              <img src={IMG.laundryHero} alt="Laundry service" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/30 to-transparent" />
              <div className="relative z-10 h-full flex flex-col justify-end p-8 text-white">
                <div className="eyebrow text-white/80">{t.laundry.eyebrow}</div>
                <h3 className="font-display text-3xl mt-2 max-w-md">{t.laundry.title}</h3>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/laundry" className="inline-flex items-center gap-2 rounded-full bg-white text-ink px-5 py-2.5 text-sm hover:bg-cream">
                    {t.common.readMore}
                  </Link>
                  <a href={waUrl(LAUNDRY_MESSAGE())} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-wa text-white px-5 py-2.5 text-sm">
                    WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Gallery strip */}
      <section className="pb-24" data-testid="section-gallery">
        <div className="container-px mx-auto">
          <SectionTitle eyebrow="Gallery" title="Postcards from Pererenan" />
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[IMG.g1, IMG.g2, IMG.g3, IMG.g4, IMG.g5, IMG.g6].map((src, i) => (
              <motion.div
                key={src}
                initial={{ opacity: 0, scale: 0.96 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.04 }}
                className={`overflow-hidden rounded-2xl ${i % 3 === 0 ? "row-span-2 aspect-[3/4]" : "aspect-square"}`}
              >
                <img src={src} alt="" className="w-full h-full object-cover hover:scale-110 transition-transform duration-700" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
